import i18n from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import { initReactI18next } from 'react-i18next'
import { uiLocales, UI_NAMESPACES } from '@wehunt/ui'
import commonEn from './locales/en.json'
import commonSv from './locales/sv.json'
import commonFi from './locales/fi.json'
import commonNo from './locales/no.json'
import commonDe from './locales/de.json'
import commonFr from './locales/fr.json'
import commonDa from './locales/da.json'

// wehunt-web localization foundation (CLUB-273).
//
// Unlike the viltrapport clubhouse host — which registers per-feature bundles
// via addResourceBundle() and therefore cares about module evaluation order —
// wehunt-web starts with a single `common` namespace whose resources are
// passed inline to .init(). Feature modules do NOT touch i18next at module
// scope, so importing this singleton is enough to have everything ready; there
// is no ordering hazard to guard against yet. Add feature namespaces the same
// inline way (or switch to addResourceBundle) as the app grows.
//
// The wehunt-only page components (TeamsPage, TeamDetailPage, HuntsPage, …) live
// in @wehunt/ui and own their locale bundles there; we register those bundles
// below via addResourceBundle (deep-merged into `common`, plus their own
// namespaces like `teams`). App-side feature strings (routes, plan wizard) get
// their own domain locales/<f>/{en,sv,fi}.json registered the inline way.
//
// Language is auto-detected from the browser/system (navigator), cached in
// localStorage. If the detected language is one of the supported set it is used;
// otherwise — and for any key not yet translated in the active language — we
// fall back to English (the authored source of the copy, and the most complete
// bundle). So a French/Spanish/… system shows English, while sv/fi/no/nb/nn/de
// systems show their language. Order: active language -> en.

const isDev = import.meta.env?.DEV === true

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    // Bokmål and Nynorsk browsers report nb/nn, which i18next does not treat as
    // variants of `no`; without these entries they fell back to English. Both
    // read the Norwegian bundles; the code stays nb/nn so callers that care
    // about the visitor's language (the shop-site rule in the app shell) see it.
    fallbackLng: { nb: ['no', 'en'], nn: ['no', 'en'], default: ['en'] },
    supportedLngs: ['en', 'sv', 'fi', 'no', 'nb', 'nn', 'de', 'fr', 'da'],
    // Map region variants (de-AT, en-US, sv-SE, …) to the base language so system
    // locales with a region still resolve to a supported language.
    load: 'languageOnly',
    nonExplicitSupportedLngs: true,
    ns: Array.from(new Set(['common', ...UI_NAMESPACES])),
    defaultNS: 'common',
    resources: {
      en: { common: commonEn },
      sv: { common: commonSv },
      fi: { common: commonFi },
      no: { common: commonNo },
      de: { common: commonDe },
      fr: { common: commonFr },
      da: { common: commonDa },
    },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'lang',
      caches: ['localStorage'],
    },
    interpolation: { escapeValue: false },
    saveMissing: isDev,
    missingKeyHandler: isDev
      ? (lngs, ns, key, fallbackValue) => {
          // i18next passes the caller's `defaultValue` as `fallbackValue` when one
          // was supplied to t(), and the bare key otherwise. A supplied default is
          // the author deliberately handling a key that may not resolve (e.g. an
          // id-keyed lookup with a catalogue value as the fallback) — that is a
          // handled case, not the unhandled miss this dev-only gate exists to
          // catch, so only throw when there was no real default to fall back to.
          if (typeof fallbackValue === 'string' && fallbackValue !== key) return
          // eslint-disable-next-line no-console -- dev-only signal for missing keys
          console.error(`[i18n] missing key ${ns}:${key} (langs: ${lngs.join(',')})`)
          throw new Error(`Missing i18n key: ${ns}:${key}`)
        }
      : undefined,
  })

// Register the @wehunt/ui page-component bundles. deepMerge=true folds the lib's
// `common` (actions.*) into the domain `common` (language.*); overwrite=false so
// domain keys win on any collision. Feature namespaces (teams, …) are added as-is.
for (const [lng, bundles] of Object.entries(uiLocales)) {
  for (const [ns, resources] of Object.entries(bundles)) {
    i18n.addResourceBundle(lng, ns, resources, true, false)
  }
}

export { i18n }
